import { required, TextInput, useRecordContext, useTranslate } from 'react-admin';
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { CHAIN_NAME_BY_ID } from './constants';

const chainChoices = Object.entries(CHAIN_NAME_BY_ID).map(([id, name]) => ({
  id,
  name: `${id} (${name})`,
}));

// Keeps digits and at most one decimal point, so the field never holds a non-numeric string.
const parseDecimal = (value: string) => {
  if (!value) return '';
  const [head, ...rest] = value.replace(/[^0-9.]/g, '').split('.');
  return rest.length ? `${head}.${rest.join('')}` : head;
};

// Keeps digits only, for whole-number fields.
const parseInteger = (value: string) => (value ? value.replace(/[^0-9]/g, '') : '');

// Rejects zero/negative; empty is left to required() so the two compose cleanly.
const positive = (value: any) =>
  value === '' || value == null || Number(value) > 0
    ? undefined
    : 'module.xbitRelayWithdrawalProviderConfigs.mustBePositive';

export const feeFieldKey = (chainId: string) => `chainFees.id_${chainId}`;

const ChainWithdrawalFeesTable = () => {
  const t = useTranslate();
  const record = useRecordContext<any>();
  // The API may return the fees list / its items in camelCase or snake_case; read both.
  const existing: any[] = record?.chainWithdrawalFees ?? record?.chain_withdrawal_fees ?? [];
  const feeFor = (chainId: string) => {
    const found = existing.find((fee) => String(fee?.chainId ?? fee?.chain_id) === chainId);
    return found?.flatFee ?? found?.flat_fee ?? '';
  };

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">
        {t('module.xbitRelayWithdrawalProviderConfigs.chainWithdrawalFees')}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.xbitRelayWithdrawalProviderConfigs.chainId')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.xbitRelayWithdrawalProviderConfigs.flatFee')} (USD)
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {chainChoices.map((chain) => (
            <TableRow key={chain.id}>
              <TableCell sx={{ width: '45%' }}>
                <Typography variant="body2">{chain.name}</Typography>
              </TableCell>
              <TableCell>
                <TextInput
                  source={feeFieldKey(chain.id)}
                  defaultValue={feeFor(chain.id)}
                  label={false}
                  parse={parseDecimal}
                  inputProps={{ inputMode: 'decimal' }}
                  helperText={false}
                  size="small"
                  fullWidth
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
};

const WithdrawalProviderConfigForm = () => {
  const t = useTranslate();

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <TextInput
        source="autoApprovalMaxAmount"
        label={t('module.xbitRelayWithdrawalProviderConfigs.autoApprovalMaxAmount')}
        parse={parseDecimal}
        inputProps={{ inputMode: 'decimal' }}
        validate={[required(), positive]}
        fullWidth
      />
      <TextInput
        source="frequencyWindowMin"
        label={t('module.xbitRelayWithdrawalProviderConfigs.frequencyWindowMin')}
        parse={parseInteger}
        inputProps={{ inputMode: 'numeric' }}
        validate={[required(), positive]}
        fullWidth
      />
      <TextInput
        source="frequencyMaxRequests"
        label={t('module.xbitRelayWithdrawalProviderConfigs.frequencyMaxRequests')}
        parse={parseInteger}
        inputProps={{ inputMode: 'numeric' }}
        validate={[required(), positive]}
        fullWidth
      />

      <ChainWithdrawalFeesTable />
    </Stack>
  );
};

export default WithdrawalProviderConfigForm;
